// IE Hub - United Utilities Date Solution Component
// Captures benefit start dates (Month & Year) with responsive desktop/mobile UX

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  HelpOutlineIcon,
  CloseIcon,
  TextField,
  FormControl,
  styled,
} from '../../../ui';
import {
  Autocomplete,
  TextField as MuiTextField,
  AutocompleteRenderInputParams,
} from '@mui/material';
import { UU_SHOW_HIDE_INFO } from '@/shared/uuShowHide';

// Styled components for wheel picker
const WheelPickerContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '24px',
  padding: '16px 0',
});

const WheelColumn = styled(Box)({
  position: 'relative',
  height: '200px',
  width: '120px',
  overflow: 'hidden',
});

const WheelScroller = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  overflowY: 'auto',
  height: '100%',
  scrollSnapType: 'y mandatory',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
});

const WheelItem = styled(Box)<{ isSelected?: boolean }>(({ isSelected }) => ({
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  scrollSnapAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  fontSize: isSelected ? '1.25rem' : '1rem',
  fontWeight: isSelected ? 700 : 400,
  color: isSelected ? '#000' : '#999',
  opacity: isSelected ? 1 : 0.6,
}));

const SelectionHighlight = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 0,
  height: '40px',
  transform: 'translateY(-50%)',
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  pointerEvents: 'none',
  zIndex: 1,
}));

const GradientOverlay = styled(Box)<{ gradientPosition: 'top' | 'bottom' }>(({ gradientPosition }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  height: '60px',
  pointerEvents: 'none',
  zIndex: 2,
  ...(gradientPosition === 'top' && {
    top: 0,
    background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
  }),
  ...(gradientPosition === 'bottom' && {
    bottom: 0,
    background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
  }),
}));

export interface DateSolutionUUProps {
  /** Label text (default: "What date did this benefit start?") */
  label?: string;
  /** Context/help text shown below label */
  contextText?: string;
  /** Tooltip text for help icon */
  helpTooltip?: string;
  /** Callback when help icon is clicked (opens help slider) */
  onHelpClick?: () => void;
  /** Callback when date is selected/changed */
  onChange?: (month: string, year: string) => void;
  /** Initial month value (e.g., "January") */
  initialMonth?: string;
  /** Initial year value (e.g., "2025") */
  initialYear?: string;
  /** Disable the input fields */
  disabled?: boolean;
  /** Show error state */
  error?: boolean;
  /** Error message text */
  errorText?: string;
  /** Whether the field is required (shows red asterisk next to label) */
  required?: boolean;
  /** Custom styling */
  sx?: Record<string, unknown>;
}

// Month options for dropdown - short names for wheel picker
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Short month names for wheel picker display
const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Generate year options (current year down to 108 years ago).
// 108 years covers the oldest possible employment start; if a DOB-based
// minimum is available the caller can filter further.
const generateYearOptions = (): string[] => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 108;
  const years: string[] = [];

  for (let year = currentYear; year >= startYear; year--) {
    years.push(year.toString());
  }

  return years;
};

// Wheel Picker Component
interface WheelPickerProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

const WheelPicker: React.FC<WheelPickerProps> = ({ options, value, onChange }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const itemHeight = 40;
  const visibleItems = 5;
  const paddingItems = Math.floor(visibleItems / 2);

  // Create padded options array for scroll effect
  const paddedOptions = useMemo(() => {
    const padding = Array(paddingItems).fill('');
    return [...padding, ...options, ...padding];
  }, [options, paddingItems]);

  const selectedIndex = options.indexOf(value);

  // Scroll to selected item on mount and value change
  useEffect(() => {
    if (scrollerRef.current && selectedIndex >= 0) {
      const scrollPosition = selectedIndex * itemHeight;
      scrollerRef.current.scrollTop = scrollPosition;
    }
  }, [selectedIndex, itemHeight]);

  // Handle scroll end to snap to nearest item
  const handleScroll = useCallback(() => {
    if (scrollerRef.current) {
      const scrollTop = scrollerRef.current.scrollTop;
      const index = Math.round(scrollTop / itemHeight);
      const clampedIndex = Math.max(0, Math.min(index, options.length - 1));
      
      if (options[clampedIndex] && options[clampedIndex] !== value) {
        onChange(options[clampedIndex]);
      }
    }
  }, [options, value, onChange, itemHeight]);

  // Debounced scroll handler
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleScrollDebounced = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(handleScroll, 100);
  }, [handleScroll]);

  // Handle item click
  const handleItemClick = useCallback((option: string, index: number) => {
    if (option && scrollerRef.current) {
      const actualIndex = index - paddingItems;
      if (actualIndex >= 0 && actualIndex < options.length) {
        onChange(option);
        scrollerRef.current.scrollTop = actualIndex * itemHeight;
      }
    }
  }, [onChange, paddingItems, options.length, itemHeight]);

  return (
    <WheelColumn>
      <GradientOverlay gradientPosition="top" />
      <GradientOverlay gradientPosition="bottom" />
      <SelectionHighlight />
      <WheelScroller ref={scrollerRef} onScroll={handleScrollDebounced}>
        {paddedOptions.map((option, index) => {
          const isSelected = option === value;
          
          return (
            <WheelItem
              key={`${option}-${index}`}
              isSelected={isSelected}
              onClick={() => handleItemClick(option, index)}
              sx={{
                visibility: option ? 'visible' : 'hidden',
              }}
            >
              {option}
            </WheelItem>
          );
        })}
      </WheelScroller>
    </WheelColumn>
  );
};

const DateSolutionUU: React.FC<DateSolutionUUProps> = ({
  label = 'What date did this benefit start?',
  contextText = 'Please provide the start date for this specific benefit. You can usually find this on your entitlement letter.',
  helpTooltip = 'Click for more information about finding your benefit start date',
  onHelpClick,
  onChange,
  initialMonth = '',
  initialYear = '',
  disabled = false,
  required = false,
  error = false,
  errorText = '',
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedMonth, setSelectedMonth] = useState<string>(initialMonth);
  const [selectedYear, setSelectedYear] = useState<string>(initialYear);
  const [mobileModalOpen, setMobileModalOpen] = useState<boolean>(false);
  const [tempMonth, setTempMonth] = useState<string>(initialMonth);
  const [tempYear, setTempYear] = useState<string>(initialYear);

  useEffect(() => {
    setSelectedMonth(initialMonth);
    setTempMonth(initialMonth);
  }, [initialMonth]);

  useEffect(() => {
    setSelectedYear(initialYear);
    setTempYear(initialYear);
  }, [initialYear]);

  const yearOptions = useMemo(() => generateYearOptions(), []);

  const emitChangeIfComplete = useCallback(
    (nextMonth: string, nextYear: string) => {
      if (!onChange) return;
      
      // Call onChange when both fields are filled OR both are empty (to clear)
      if ((nextMonth && nextYear) || (!nextMonth && !nextYear)) {
        onChange(nextMonth, nextYear);
      }
    },
    [onChange]
  );

  const handleYearAutocompleteChange = useCallback(
    (_event: React.SyntheticEvent, value: string | null) => {
      const year = value?.trim() ?? '';
      setSelectedYear(year);
      
      // If year is cleared, also clear month (requirement: clearing one clears both)
      if (!year && selectedMonth) {
        setSelectedMonth('');
        emitChangeIfComplete('', '');
      } else {
        emitChangeIfComplete(selectedMonth, year);
      }
    },
    [emitChangeIfComplete, selectedMonth]
  );

  // Autocomplete handler for desktop month field
  const handleMonthAutocompleteChange = useCallback(
    (_event: React.SyntheticEvent, value: string | null) => {
      const month = value || '';
      setSelectedMonth(month);
      
      // If month is cleared, also clear year (requirement: clearing one clears both)
      if (!month && selectedYear) {
        setSelectedYear('');
        emitChangeIfComplete('', '');
      } else {
        emitChangeIfComplete(month, selectedYear);
      }
    },
    [emitChangeIfComplete, selectedYear]
  );

  // Mobile handlers
  const handleMobileFieldClick = useCallback(() => {
    if (!disabled) {
      setTempMonth(selectedMonth);
      setTempYear(selectedYear);
      setMobileModalOpen(true);
    }
  }, [disabled, selectedMonth, selectedYear]);

  const handleMobileSave = useCallback(() => {
    // If either field is empty in mobile, clear both (requirement: clearing one clears both)
    const finalMonth = tempMonth && tempYear ? tempMonth : '';
    const finalYear = tempMonth && tempYear ? tempYear : '';
    
    setSelectedMonth(finalMonth);
    setSelectedYear(finalYear);
    emitChangeIfComplete(finalMonth, finalYear);
    setMobileModalOpen(false);
  }, [emitChangeIfComplete, tempMonth, tempYear]);

  const handleMobileCancel = useCallback(() => {
    setTempMonth(selectedMonth);
    setTempYear(selectedYear);
    setMobileModalOpen(false);
  }, [selectedMonth, selectedYear]);

  // Wheel picker handlers
  const handleWheelMonthChange = useCallback((month: string) => {
    // Convert short month name to full month name
    const monthIndex = MONTHS_SHORT.indexOf(month);
    if (monthIndex >= 0 && MONTHS[monthIndex]) {
      setTempMonth(MONTHS[monthIndex]);
    }
  }, []);

  const handleWheelYearChange = useCallback((year: string) => {
    setTempYear(year);
  }, []);

  // Get short month name for wheel picker
  const getTempMonthShort = useMemo((): string => {
    const monthIndex = MONTHS.indexOf(tempMonth);
    return monthIndex >= 0 && MONTHS_SHORT[monthIndex] ? MONTHS_SHORT[monthIndex] : '';
  }, [tempMonth]);

  // Format display value for mobile
  const displayValue = useMemo(() => {
    if (selectedMonth && selectedYear) {
      return `${selectedMonth} ${selectedYear}`;
    }
    return '';
  }, [selectedMonth, selectedYear]);

  return (
    <>
    { UU_SHOW_HIDE_INFO.uuShowHideInfo ?
    null : (
    <Box
      sx={{
        mt: 1.5,
        mb: 1.5,
        py: 1.25,
        pl: 2,
        pr: 2.5,
        borderLeft: '4px solid',
        borderLeftColor: 'info.main',
        backgroundColor: 'transparent',
        borderRadius: '0 8px 8px 0', // Rounded corners on right side only
        ...sx,
      }}
    >
      {/* Label and Help Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.5,
          mb: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.875rem',
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          )}
        </Typography>

        <Tooltip title={helpTooltip} placement="top" arrow>
          <IconButton
            size="small"
            onClick={onHelpClick}
            sx={{
              width: 24,
              height: 24,
              color: onHelpClick ? 'primary.main' : 'text.secondary',
              flexShrink: 0,
              ml: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            aria-label="Help information"
          >
            <HelpOutlineIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Context Text */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: '0.813rem',
          mb: 1.5,
          lineHeight: 1.5,
        }}
      >
        {contextText}
      </Typography>

      {/* Desktop View: Two Separate Dropdowns */}
      {!isMobile ? (
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          {/* Month Field (Searchable Autocomplete) */}
          <FormControl error={error} sx={{ flex: 1, minWidth: 0 }}>
            <Autocomplete
              options={MONTHS}
              value={selectedMonth || null}
              onChange={handleMonthAutocompleteChange}
              disabled={disabled}
              size="small"
              fullWidth
              renderInput={(params: AutocompleteRenderInputParams) => {
                // TypeScript workaround for exactOptionalPropertyTypes with MUI Autocomplete
                // See: https://github.com/mui/material-ui/issues/18757
                return (
                  // @ts-ignore - MUI Autocomplete params have optional properties that conflict with exactOptionalPropertyTypes
                  <MuiTextField
                    {...params}
                    {...(selectedMonth ? {} : { label: 'Select Month' })}
                    error={error}
                    placeholder="Type or select..."
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                );
              }}
              sx={{
                width: '100%',
                '& .MuiAutocomplete-input': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </FormControl>

          {/* Year Field (Standard Dropdown) */}
          <FormControl error={error} sx={{ flex: 1, minWidth: 0 }}>
            <Autocomplete
              options={yearOptions}
              value={selectedYear || null}
              onChange={handleYearAutocompleteChange}
              disabled={disabled}
              size="small"
              fullWidth
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params: AutocompleteRenderInputParams) => {
                return (
                  // @ts-ignore - MUI Autocomplete params have optional properties that conflict with exactOptionalPropertyTypes
                  <MuiTextField
                    {...params}
                    {...(selectedYear ? {} : { label: 'Select Year' })}
                    error={error}
                    placeholder="Type or select..."
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                );
              }}
              sx={{
                width: '100%',
                '& .MuiAutocomplete-input': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </FormControl>
        </Box>
      ) : (
        /* Mobile View: Single Field with Modal Picker */
        <Box>
          <TextField
            fullWidth
            value={displayValue}
            placeholder="Select the Month/Year"
            onClick={handleMobileFieldClick}
            disabled={disabled}
            error={error}
            InputProps={{
              readOnly: true,
              sx: {
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
              },
              endAdornment: (
                <Box
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    pr: 0.5,
                  }}
                >
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Box>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover': {
                  cursor: disabled ? 'not-allowed' : 'pointer',
                },
              },
            }}
          />

          {/* Mobile Modal with Wheel Pickers */}
          <Dialog
            open={mobileModalOpen}
            onClose={handleMobileCancel}
            fullWidth
            maxWidth="xs"
            PaperProps={{
              sx: {
                position: 'fixed',
                bottom: 0,
                m: 0,
                borderRadius: '16px 16px 0 0',
                maxHeight: '420px',
                width: '100%',
                maxWidth: '100%',
                boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            {/* Custom Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2.5,
                pt: 2.5,
                pb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: 'text.primary',
                }}
              >
                {label}
              </Typography>
              <IconButton
                onClick={handleMobileCancel}
                size="small"
                sx={{
                  color: 'text.secondary',
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Wheel Picker Area */}
            <Box sx={{ px: 2, py: 2 }}>
              <WheelPickerContainer>
                {/* Month Wheel */}
                <WheelPicker
                  options={MONTHS_SHORT}
                  value={getTempMonthShort || ''}
                  onChange={handleWheelMonthChange}
                />
                
                {/* Year Wheel */}
                <WheelPicker
                  options={yearOptions}
                  value={tempYear}
                  onChange={handleWheelYearChange}
                />
              </WheelPickerContainer>
            </Box>

            {/* Bottom Action Buttons */}
            <DialogActions
              sx={{
                px: 2.5,
                pb: 2.5,
                pt: 1,
                gap: 1.5,
              }}
            >
              <Button
                onClick={handleMobileCancel}
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  py: 1.25,
                  px: 3,
                  borderRadius: 1.5,
                  borderColor: '#E0E0E0',
                  color: 'text.primary',
                  minWidth: '100px',
                  '&:hover': {
                    borderColor: '#BDBDBD',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleMobileSave}
                variant="contained"
                disabled={(!tempMonth && tempYear) || (tempMonth && !tempYear)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  py: 1.25,
                  px: 3,
                  borderRadius: 1.5,
                  backgroundColor: '#B8D431',
                  color: '#000',
                  minWidth: '100px',
                  boxShadow: 'none',
                  flex: 1,
                  '&:hover': {
                    backgroundColor: '#A8C42B',
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    backgroundColor: '#E0E0E0',
                    color: '#9E9E9E',
                  },
                }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Error Message */}
      {error && errorText && (
        <Typography
          variant="caption"
          sx={{
            color: 'error.main',
            mt: 0.5,
            display: 'block',
            fontSize: '0.75rem',
          }}
        >
          {errorText}
        </Typography>
      )}
    </Box>
  )}
  </>
  );
};

export default DateSolutionUU;

